/**
 * Auth service using Cognito Hosted UI with Microsoft federation.
 * Uses Authorization Code flow with PKCE (no client secret needed in browser).
 */

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI || `${window.location.origin}/callback`;
const LOGOUT_URI = import.meta.env.VITE_LOGOUT_URI || window.location.origin;

export interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

// ─── PKCE helpers ─────────────────────────────────────────────────────────────

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// ─── Login ────────────────────────────────────────────────────────────────────

export async function loginWithMicrosoft(): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateCodeVerifier(); // random state for CSRF

  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    identity_provider: 'Microsoft',
  });

  window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
}

// ─── Callback ─────────────────────────────────────────────────────────────────

export async function handleCallback(code: string, state: string): Promise<TokenSet> {
  const savedState = sessionStorage.getItem('oauth_state');
  const verifier = sessionStorage.getItem('pkce_verifier');

  if (state !== savedState) {
    throw new Error('OAuth state mismatch — possible CSRF attack');
  }

  if (!verifier) {
    throw new Error('PKCE verifier not found');
  }

  const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    const err = await response.text();
    throw new Error(`Token exchange failed: ${err}`);
  }

  const data = await response.json();

  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oauth_state');

  const tokens: TokenSet = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };

  saveTokens(tokens);
  return tokens;
}

// ─── Token storage ────────────────────────────────────────────────────────────

export function saveTokens(tokens: TokenSet): void {
  localStorage.setItem('auth_tokens', JSON.stringify(tokens));
}

export function getTokens(): TokenSet | null {
  const raw = localStorage.getItem('auth_tokens');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  localStorage.removeItem('auth_tokens');
}

export function isAuthenticated(): boolean {
  const tokens = getTokens();
  if (!tokens) return false;
  return tokens.expiresAt > Date.now();
}

export function getAccessToken(): string | null {
  const tokens = getTokens();
  if (!tokens || tokens.expiresAt <= Date.now()) return null;
  return tokens.accessToken;
}

export function getIdToken(): string | null {
  const tokens = getTokens();
  if (!tokens || tokens.expiresAt <= Date.now()) return null;
  return tokens.idToken;
}

// ─── User info from ID token ──────────────────────────────────────────────────

export function getUserFromToken(): { email: string; name: string; sub: string } | null {
  const idToken = getIdToken();
  if (!idToken) return null;

  try {
    const payload = idToken.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      email: decoded.email || decoded['cognito:username'] || '',
      name: decoded.name || decoded.given_name || decoded.email || '',
      sub: decoded.sub || '',
    };
  } catch {
    return null;
  }
}

// ─── Logout ───────────────────────────────────────────────────────────────────

export function logout(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: LOGOUT_URI,
  });
  window.location.href = `${COGNITO_DOMAIN}/logout?${params}`;
}
